import i18n from 'i18next';
import toast from 'react-hot-toast';

import { MutationCache, QueryCache, QueryClient, QueryKey, matchQuery } from '@tanstack/react-query';

import { getErrorStatus } from '../data/errors.ts';

interface MutationMeta extends Record<string, unknown> {
    invalidates?: QueryKey[];
    resetQueries?: boolean;
    skipInvalidateQueries?: boolean;
}

declare module '@tanstack/react-query' {
    interface Register {
        mutationMeta: MutationMeta;
    }
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                const status = getErrorStatus(error);
                return !(status && status >= 400 && status < 500) && failureCount < 2;
            },
            // Keep usable cached content on screen when a background refresh fails.
            throwOnError: (_error, query) => query.state.data === undefined,
        },
    },
    queryCache: new QueryCache({
        onError: (_error, query) => {
            if (query.state.data !== undefined) {
                toast.error(i18n.t('components:requestError.refreshFailed'), { id: 'query-refresh-error' });
            }
        },
    }),
    mutationCache: new MutationCache({
        onSuccess: async (_data, _variables, _context, mutation) => {
            const meta = mutation.options.meta;
            if (meta?.skipInvalidateQueries) return;

            // Identity changes also discard inactive data belonging to the previous viewer.
            if (meta?.resetQueries) {
                await queryClient.cancelQueries();
                await queryClient.resetQueries();
                return;
            }

            const keys = meta?.invalidates ?? [];
            if (!keys.length) return;

            await queryClient.invalidateQueries({
                predicate: (query) => keys.some((queryKey) => matchQuery({ queryKey }, query)),
            });
        },
    }),
});
